/**
 * The peek (docs/ux/PEEK-AND-RESUME.md §4.2; core landed.ts): a look at where a save went WITHOUT leaving the screen you're
 * on. One screen, at most 8 rows of the destination, the new row first and ringed for 1.2 s. A page is never rendered inside
 * it: the rows are the destination's own summary (the order list's items; today's records, which the server scopes: staff
 * see only their own rows, managers everyone's). Staff see the order list read-only. The shared tablet never peeks.
 *
 * History contract (web): opening pushes one entry (same URL, same router index); X, the scrim, Esc and "Back to …" go back
 * one entry, so the next browser Back is the screen before, never a dead entry. "Open the full list" replaces that entry
 * and goes to the page; Back from there returns here with the screen as it was (scroll, step, form: nothing under the
 * sheet unmounts).
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DESTINATIONS, peekKicker, peekRows, recordsForDestination, type LandedEvent, type TodayRecordRow, type OrderItem } from "@logwave/core";
import { useApi, useSession } from "../session.tsx";
import { Icon } from "./Icons.tsx";
import { Button, ErrorBox, Sheet, Skeleton, usePeekRequest } from "./ui.tsx";

type Row = { id: string; title: string; line: string };

function useRows(e: LandedEvent): { rows?: Row[]; error?: string; reload: () => void } {
  const api = useApi();
  const s = useSession();
  const [state, setState] = useState<{ rows?: Row[]; error?: string }>({});
  const [n, setN] = useState(0);
  useEffect(() => {
    let live = true;
    const load = async (): Promise<Row[]> => {
      if (e.destination === "order_list") {
        const d = await api.ordering(s.org.id, s.location.id);
        return d.groups.flatMap((g) => g.items.map((i: OrderItem) => ({ id: i.key, title: i.name, line: [i.whyText, g.title].filter(Boolean).join(" · ") })));
      }
      const d = await api.todayRecords(s.org.id, s.location.id);
      // That log's own rows (core recordsForDestination: never a daily check-in, T4).
      return recordsForDestination(e.destination, d.rows).map((r: TodayRecordRow) => ({ id: r.id, title: r.title, line: [r.time, r.line, r.who].filter(Boolean).join(" · ") }));
    };
    load().then((rows) => { if (live) setState({ rows }); }, (err: unknown) => { if (live) setState({ error: err instanceof Error ? err.message : String(err) }); });
    return () => { live = false; };
  }, [api, s.org.id, s.location.id, e.destination, n]);
  return { ...state, reload: () => setN((x) => x + 1) };
}

/** Mounted once inside the session and the router (Layout); opens when a Landed card is tapped. */
export function PeekHost() {
  const { req, close } = usePeekRequest();
  if (!req || req.viewer.kiosk) return null;
  return <PeekSheet key={req.events.map((x) => x.id).join()} events={req.events} where={req.where} back={req.back} onClosed={close} />;
}

function PeekSheet({ events, where, back, onClosed }: { events: LandedEvent[]; where: string; back: string; onClosed: () => void }) {
  const nav = useNavigate();
  const e = events[events.length - 1]!;
  const d = DESTINATIONS[e.destination];
  const data = useRows(e);
  const pushed = useRef(false);
  const leaving = useRef<"back" | "go" | null>(null);
  // One history entry while the peek is open; a Back with no peek state closes it.
  useEffect(() => {
    // Once, even when React runs this effect twice (development StrictMode): two entries would need two Backs.
    if (!pushed.current && !leaving.current) { try { window.history.pushState({ ...(window.history.state ?? {}), peek: e.id }, ""); pushed.current = true; } catch { /* no history: X still closes */ } }
    // Closed by any Back that leaves THIS peek's entry, even onto an entry an older peek left (a reload keeps history state).
    const onPop = (ev: PopStateEvent) => { if ((ev.state as { peek?: string } | null)?.peek !== e.id) { pushed.current = false; onClosed(); } };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const shut = () => {
    if (leaving.current) return;
    leaving.current = "back";
    if (pushed.current) { pushed.current = false; window.history.back(); } // popstate → onClosed
    else onClosed();
  };
  const openFull = () => {
    leaving.current = "go";
    if (pushed.current) { try { const st = { ...(window.history.state ?? {}) } as Record<string, unknown>; delete st.peek; window.history.replaceState(st, ""); } catch { /* ignore */ } pushed.current = false; }
    onClosed();
    nav(d.route);
  };
  const ids = new Set(events.map((x) => x.recordId).filter(Boolean) as string[]);
  const isNew = (r: Row) => ids.has(r.id) || [...ids].some((id) => r.id.endsWith(`:${id}`));
  const picked = data.rows ? peekRows(data.rows, isNew) : undefined;
  // No exact id (a record saved before the server answered): the newest row is the one just saved.
  const ring = picked ? (picked.ringed >= 0 ? picked.ringed : e.destination === "order_list" ? -1 : 0) : -1;
  return (
    <Sheet title={d.title} kicker={peekKicker(where)} onClose={shut} closeLabel="Close the peek">
      <div className="stack peek" data-testid="peek" data-destination={e.destination}>
        {!picked ? (data.error ? <ErrorBox error={`We couldn't load the ${d.short}. ${data.error}`} onRetry={data.reload} /> : <Skeleton rows={3} label={`Loading the ${d.short}`} />) : picked.rows.length === 0 ? (
          <p className="small">Nothing here yet.</p>
        ) : (
          <ul className="plain peek-rows" aria-label={d.title}>
            {picked.rows.map((r, k) => (
              <li key={r.id} className={`peek-row${k === ring ? " peek-new" : ""}`} {...(k === ring ? { "aria-current": "true" as const } : {})}>
                <Icon name={d.picture} size={18} />
                <span><strong>{r.title}</strong>{r.line ? <span className="caption"> · {r.line}</span> : null}</span>
              </li>
            ))}
          </ul>
        )}
        <p className="caption peek-path">Also at: {d.path}</p>
        <div className="peek-foot">
          <Button variant="secondary" icon="external" onClick={openFull}>Open the full list</Button>
          <Button icon="back" data-next onClick={shut}>{`Back to ${back}`}</Button>
        </div>
      </div>
    </Sheet>
  );
}
